import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NotificationService } from '@/pages/service/core/notification.service';
import { TimezoneService } from '@/pages/service/core/timezone.service';
import { hasAnyRole } from './role.util';

/**
 * Global notification bell — extracted from the topbar so it can mount in EITHER the
 * topbar (mobile) OR the sidebar rail (desktop, where the topbar is hidden). One instance
 * per context; only the visible one ever renders. Polls unread every 10s, paginates the
 * list, marks read on click, and deep-links via the notification's `link`.
 *
 * `variant="sidebar"` opens the (position:fixed) panel right next to the bell button so the
 * sidebar's overflow can't clip it and the rail's width doesn't matter.
 */
@Component({
    selector: 'app-notification-bell',
    standalone: true,
    imports: [CommonModule],
    template: `
    <div class="nbell" [class.nbell--sidebar]="variant === 'sidebar'" [class.nbell--floating]="variant === 'floating'"
         *ngIf="showBell" (click)="$event.stopPropagation()">
        <button #bellBtn type="button" class="nbell__btn" (click)="togglePanel()" aria-label="Notifications" title="Notifications">
            <i class="pi pi-bell"></i>
            <span class="nbell__badge" *ngIf="unread > 0">{{ unread > 99 ? '99+' : unread }}</span>
        </button>

        <div class="nbell__panel" *ngIf="panelOpen" [ngStyle]="panelStyle">
            <div class="nbell__head">
                <span>Notifications</span>
                <button *ngIf="unread > 0" type="button" class="nbell__markall" (click)="markAllRead()">Mark all read</button>
            </div>
            <div class="nbell__list">
                <div *ngIf="loading && !items.length" class="nbell__empty">Loading…</div>
                <div *ngIf="!loading && !items.length" class="nbell__empty">You're all caught up.</div>
                <button *ngFor="let n of items" type="button" class="nbell__item" [class.is-unread]="!n.seen" (click)="open(n)">
                    <i class="pi nbell__icon" [ngClass]="iconFor(n.type)"></i>
                    <div class="nbell__item-body">
                        <div class="nbell__item-title">{{ n.title }}</div>
                        <div class="nbell__item-sub" *ngIf="n.body">{{ n.body }}</div>
                        <div class="nbell__item-time">{{ timeAgo(n.createdAt) }}</div>
                    </div>
                    <span class="nbell__udot" *ngIf="!n.seen"></span>
                </button>
            </div>
            <button *ngIf="hasMore" type="button" class="nbell__more" [disabled]="loading" (click)="loadMore()">
                {{ loading ? 'Loading…' : 'Show more' }}
            </button>
        </div>
    </div>`,
    styles: [`
        .nbell { position: relative; display: inline-flex; }
        .nbell__btn { position: relative; width: 40px; height: 40px; border: none; background: transparent; border-radius: var(--gh-r-md); cursor: pointer; color: var(--text-color, var(--gh-text-2)); display: inline-flex; align-items: center; justify-content: center; }
        .nbell__btn:hover { background: var(--surface-hover, var(--gh-surface-hover)); }
        .nbell__btn i { font-size: var(--gh-fs-19); }
        .nbell__badge { position: absolute; top: 4px; right: 4px; min-width: 17px; height: 17px; padding: 0 4px; border-radius: var(--gh-r-pill); background: var(--gh-danger); color: var(--gh-on-primary); font-size: var(--gh-fs-11); font-weight: 800; display: inline-flex; align-items: center; justify-content: center; border: 2px solid var(--surface-card, var(--gh-surface)); }
        .nbell__panel { position: absolute; right: 0; top: 48px; width: 360px; max-width: calc(100vw - 24px); max-height: 70vh; display: flex; flex-direction: column; background: var(--surface-card, var(--gh-surface)); border: 1px solid var(--surface-border, var(--gh-border)); border-radius: var(--gh-r-lg); box-shadow: 0 16px 48px rgba(15,23,42,0.18); overflow: hidden; z-index: 1200; }
        /* Sidebar rail: the panel is position:fixed (set inline from the button rect). */
        .nbell--sidebar .nbell__panel { right: auto; top: auto; }
        .nbell__head { display: flex; align-items: center; justify-content: space-between; padding: 12px 14px; border-bottom: 1px solid var(--surface-border, var(--gh-border)); font-weight: 700; font-size: var(--gh-fs-15); }
        .nbell__markall { background: none; border: none; color: var(--gh-primary); font-size: var(--gh-fs-12); font-weight: 700; cursor: pointer; }
        .nbell__list { flex: 1 1 auto; overflow-y: auto; }
        .nbell__empty { padding: 28px 16px; text-align: center; color: var(--gh-text-muted); font-size: var(--gh-fs-14); }
        .nbell__item { display: flex; align-items: flex-start; gap: 10px; width: 100%; padding: 11px 14px; border: none; border-bottom: 1px solid var(--surface-border, var(--gh-border)); background: transparent; text-align: left; cursor: pointer; }
        .nbell__item:hover { background: var(--surface-hover, var(--gh-surface-sunken)); }
        .nbell__item.is-unread { background: var(--gh-primary-wash); }
        .nbell__item.is-unread:hover { background: var(--gh-primary-wash); }
        .nbell__icon { font-size: var(--gh-fs-14); color: var(--gh-primary); margin-top: 2px; flex-shrink: 0; }
        .nbell__item-body { flex: 1; min-width: 0; }
        .nbell__item-title { font-size: var(--gh-fs-13); font-weight: 600; color: var(--gh-text); }
        .nbell__item-sub { font-size: var(--gh-fs-13); color: var(--gh-text-3); margin-top: 2px; }
        .nbell__item-time { font-size: var(--gh-fs-12); color: var(--gh-text-muted); margin-top: 3px; }
        .nbell__udot { width: 8px; height: 8px; border-radius: 50%; background: var(--gh-primary); flex-shrink: 0; margin-top: 5px; }
        .nbell__more { width: 100%; padding: 10px; border: none; border-top: 1px solid var(--surface-border, var(--gh-border)); background: transparent; color: var(--gh-primary); font-weight: 700; font-size: var(--gh-fs-13); cursor: pointer; }
        .nbell__more:hover:not(:disabled) { background: var(--gh-surface-sunken); }
        .nbell__more:disabled { color: var(--gh-text-muted); cursor: default; }
        /* On mobile the topbar / floating bell is visible, so hide the sidebar one. */
        @media (max-width: 991px) { .nbell--sidebar { display: none; } }
        /* On desktop the slim top bar carries the bell, so hide the floating one. */
        @media (min-width: 992px) { .nbell--floating { display: none; } }

        /* Floating FAB — web-chat style, fixed bottom-right; panel opens above it. */
        .nbell--floating { position: fixed; bottom: 22px; right: 22px; z-index: 1100; }
        .nbell--floating .nbell__btn { width: 54px; height: 54px; border-radius: 50%; background: var(--gh-primary); color: var(--gh-on-primary); box-shadow: 0 10px 28px rgba(5,150,105,0.42); transition: background .15s, transform .15s; }
        .nbell--floating .nbell__btn:hover { background: var(--gh-primary); transform: translateY(-1px); }
        .nbell--floating .nbell__btn i { font-size: 1.45rem; }
        .nbell--floating .nbell__badge { top: 0; right: 0; border-color: var(--gh-primary); }
        .nbell--floating .nbell__panel { position: fixed; bottom: 86px; right: 22px; top: auto; left: auto; width: 380px; }
    `]
})
export class NotificationBell implements OnInit, OnDestroy {
    @Input() variant: 'topbar' | 'sidebar' | 'floating' = 'topbar';
    @ViewChild('bellBtn') bellBtn?: ElementRef<HTMLElement>;

    showBell = !hasAnyRole('ROLE_SUPER_ADMIN');
    unread = 0;
    panelOpen = false;
    items: any[] = [];
    page = 0;
    hasMore = false;
    loading = false;
    panelStyle: any = {};
    private pollTimer: any = null;
    private prevUnread = 0;
    private firstPoll = true;
    private audioCtx?: AudioContext;
    private readonly docClick = () => { if (this.panelOpen) this.closePanel(); };

    constructor(private router: Router, private notif: NotificationService, private tz: TimezoneService) {}

    ngOnInit(): void {
        if (this.showBell) {
            this.refreshUnread();
            this.pollTimer = setInterval(() => this.refreshUnread(), 10000); // poll every 10s
        }
        document.addEventListener('click', this.docClick);
    }

    ngOnDestroy(): void {
        if (this.pollTimer) clearInterval(this.pollTimer);
        document.removeEventListener('click', this.docClick);
    }

    private refreshUnread(): void {
        this.notif.unreadCount().subscribe({
            next: (res: any) => {
                const n = res?.data?.unread ?? 0;
                // A new notification arrived since the last poll → chime (but never on the very
                // first poll, so we don't beep for notifications that were already unread).
                if (!this.firstPoll && n > this.prevUnread) this.playChime();
                this.firstPoll = false;
                this.prevUnread = n;
                this.unread = n;
            },
            error: () => {}
        });
    }

    /** A short, pleasant two-note chime via the Web Audio API (no asset needed). Best-effort —
     *  browsers gate audio behind a user gesture, so it plays once the user has interacted. */
    private playChime(): void {
        try {
            const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
            if (!Ctx) return;
            if (!this.audioCtx) this.audioCtx = new Ctx();
            const ctx = this.audioCtx!;
            if (ctx.state === 'suspended') ctx.resume();
            const now = ctx.currentTime;
            [880, 1174.66].forEach((freq, i) => {           // A5 → D6
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.value = freq;
                const t = now + i * 0.12;
                gain.gain.setValueAtTime(0.0001, t);
                gain.gain.linearRampToValueAtTime(0.18, t + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
                osc.connect(gain); gain.connect(ctx.destination);
                osc.start(t); osc.stop(t + 0.3);
            });
        } catch { /* sound is best-effort */ }
    }

    togglePanel(): void {
        this.panelOpen = !this.panelOpen;
        if (this.panelOpen) {
            this.page = 0; this.items = []; this.loadPage();
            if (this.variant === 'sidebar') {
                const r = this.bellBtn?.nativeElement?.getBoundingClientRect();
                this.panelStyle = r
                    ? { position: 'fixed', top: Math.max(8, r.top) + 'px', left: (r.right + 10) + 'px', right: 'auto' }
                    : {};
            }
        }
    }

    closePanel(): void { this.panelOpen = false; }

    private loadPage(): void {
        this.loading = true;
        this.notif.list(this.page, 10).subscribe({
            next: (res: any) => {
                this.loading = false;
                const d = res?.data ?? {};
                const incoming = d.items ?? [];
                this.items = this.page === 0 ? incoming : [...this.items, ...incoming];
                this.hasMore = !!d.hasMore;
                this.unread = d.unread ?? this.unread;
            },
            error: () => { this.loading = false; }
        });
    }

    loadMore(): void { this.page++; this.loadPage(); }

    open(n: any): void {
        if (n && !n.seen) {
            this.notif.markRead(n.id).subscribe({ next: () => {}, error: () => {} });
            n.seen = true;
            this.unread = Math.max(0, this.unread - 1);
        }
        this.closePanel();
        const dest = this.destFor(n);
        if (dest) this.router.navigateByUrl(dest);
    }

    /**
     * Where a notification opens on click. Case notifications carry `refId` = caseId, so we make
     * sure every case-related event opens that case:
     *  - if the backend gave a rich case deep link (customer flow / operation case), use it;
     *  - otherwise open the case workspace directly by id — this covers events whose link points at
     *    a list view (e.g. a resolved claim request → requests inbox) or has no link at all.
     * Non-case events (attendance map, broadcast, an incoming claim request to review) keep their link.
     */
    private destFor(n: any): string | null {
        const link: string | null = n?.link || null;
        const linkOpensCase = !!link && (link.includes('/customer-flows/') || link.includes('/operation-case/'));
        if (linkOpensCase) return link;
        if (this.isCaseType(n?.type) && n?.refId) return `/operation-case/${n.refId}`;
        return link;
    }

    /** Notification types that are about a specific case (refId = caseId). Kept in sync with the
     *  Flutter app's `_isCaseType` so tapping opens the case identically on web + mobile. */
    private isCaseType(t: string): boolean {
        return ['case_assigned', 'case_note', 'activity_logged', 'status_changed', 'location_punch',
                'follow_up_assigned', 'task_assigned', 'sla_soon', 'claim_approved', 'claim_rejected'].includes(t);
    }

    markAllRead(): void {
        this.notif.markAllRead().subscribe({
            next: () => { this.items.forEach(i => i.seen = true); this.unread = 0; },
            error: () => {}
        });
    }

    iconFor(type: string): string {
        switch (type) {
            case 'case_assigned':         return 'pi-user-plus';
            case 'follow_up_assigned':    return 'pi-bell';
            case 'task_assigned':         return 'pi-check-square';
            case 'sla_soon':              return 'pi-clock';
            case 'broadcast':             return 'pi-megaphone';
            case 'claim_request':         return 'pi-user-plus';
            case 'claim_approved':        return 'pi-check-circle';
            case 'claim_rejected':        return 'pi-times-circle';
            case 'case_note':             return 'pi-comment';
            case 'activity_logged':       return 'pi-bolt';
            case 'status_changed':        return 'pi-flag';
            case 'attendance_check_in':
            case 'attendance_check_out':  return 'pi-map-marker';
            case 'location_punch':        return 'pi-map-marker';
            default:                      return 'pi-info-circle';
        }
    }

    timeAgo(iso: string): string {
        if (!iso) return '';
        const d = this.tz.toDate(iso);
        const diff = d ? Math.floor((Date.now() - d.getTime()) / 1000) : NaN;
        if (diff < 60) return 'just now';
        if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
        if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
        return this.tz.format(iso, 'date');
    }
}
